import React from 'react'

interface BillingStatsProps {
    accountBalance: number;
    transactions: any[];
    gates: any[];
}

const BillingStats: React.FC<BillingStatsProps> = ({ accountBalance, transactions, gates }) => {
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    const getStats = () => {
        const totalTransactions = transactions.length;
        const completedTransactions = transactions.filter(t => t.status === 'completed').length;
        const pendingTransactions = transactions.filter(t => t.status === 'pending').length;
        const totalGates = gates.length;
        const activeGates = gates.filter(g => g.status === 'active').length;
        const totalTransactionAmount = transactions
            .filter(t => t.status === 'completed')
            .reduce((sum, t) => sum + (t.amount || 0), 0);

        return {
            totalTransactions,
            completedTransactions,
            pendingTransactions,
            totalGates,
            activeGates,
            totalTransactionAmount
        };
    };

    const stats = getStats();

    return (
        <div className="space-y-8">
            {/* Main Balance Card */}
            <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-2xl shadow-2xl p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white bg-opacity-10 rounded-full -translate-y-32 translate-x-32"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white bg-opacity-10 rounded-full translate-y-16 -translate-x-16"></div>
                <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-bold mb-2">Total Account Balance</h2>
                            <p className="text-blue-100">Available funds for transactions</p>
                        </div>
                        <div className="p-4 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl">
                            <i className="ri-wallet-3-line text-3xl"></i>
                        </div>
                    </div>
                    <div className="text-4xl font-bold mb-4">{formatCurrency(accountBalance)}</div>
                    <div className="flex items-center space-x-6">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                            <span className="text-sm text-blue-100">Account Active</span>
                        </div>
                        <div className="flex items-center">
                            <i className="ri-shield-check-line mr-2 text-blue-200"></i>
                            <span className="text-sm text-blue-100">Secure & Verified</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                            <i className="ri-exchange-line text-blue-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">Total</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{stats.totalTransactions}</p>
                        <p className="text-sm text-gray-600">Transactions</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-50 rounded-xl group-hover:bg-green-100 transition-colors">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Completed</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{stats.completedTransactions}</p>
                        <p className="text-sm text-gray-600">Successful</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                            <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">Volume</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(stats.totalTransactionAmount)}</p>
                        <p className="text-sm text-gray-600">Transaction Volume</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors">
                            <i className="ri-gate-line text-orange-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full">Active</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{stats.activeGates}</p>
                        <p className="text-sm text-gray-600">Payment Gates</p>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button className="flex items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 hover:from-green-100 hover:to-emerald-100 transition-all duration-200 group">
                        <div className="p-3 bg-green-100 rounded-lg mr-4 group-hover:bg-green-200 transition-colors">
                            <i className="ri-send-plane-line text-green-600 text-xl"></i>
                        </div>
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900">Send Payment</h4>
                            <p className="text-sm text-gray-600">Make a new payment</p>
                        </div>
                    </button>

                    <button className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 group">
                        <div className="p-3 bg-blue-100 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                            <i className="ri-download-line text-blue-600 text-xl"></i>
                        </div>
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900">Withdraw Funds</h4>
                            <p className="text-sm text-gray-600">Transfer to bank account</p>
                        </div>
                    </button>

                    <button className="flex items-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 hover:from-purple-100 hover:to-pink-100 transition-all duration-200 group">
                        <div className="p-3 bg-purple-100 rounded-lg mr-4 group-hover:bg-purple-200 transition-colors">
                            <i className="ri-file-chart-line text-purple-600 text-xl"></i>
                        </div>
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900">View Reports</h4>
                            <p className="text-sm text-gray-600">Transaction analytics</p>
                        </div>
                    </button>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                    <button className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                        View All
                    </button>
                </div>
                <div className="space-y-4">
                    {transactions.slice(0, 3).map((transaction, index) => (
                        <div key={index} className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                            <div className={`p-2 rounded-lg mr-4 ${
                                transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                                <i className={`ri-${transaction.type === 'credit' ? 'arrow-up' : 'arrow-down'}-line ${
                                    transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                }`}></i>
                            </div>
                            <div className="flex-1">
                                <p className="font-medium text-gray-900">{transaction.description || 'Transaction'}</p>
                                <p className="text-sm text-gray-500">{transaction.reference || 'No reference'}</p>
                            </div>
                            <div className="text-right">
                                <p className="font-semibold text-gray-900">{formatCurrency(transaction.amount || 0)}</p>
                                <p className={`text-xs px-2 py-1 rounded-full ${
                                    transaction.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                }`}>
                                    {transaction.status || 'unknown'}
                                </p>
                            </div>
                        </div>
                    ))}
                    {transactions.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <i className="ri-inbox-line text-4xl mb-4"></i>
                            <p>No recent transactions</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BillingStats;